import { supabase } from "@/integrations/supabase/client";
import { isLaunchLessonId } from "@/lib/launch/launchExperienceRoster";

export type AdaptiveTier = "easy" | "medium" | "hard" | "expert";

export interface AdaptiveQuestion {
  question_id: string;
  adaptive_tier?: string | null;
  difficulty?: string | null;
  concept_tag?: string | null;
  [key: string]: unknown;
}

const TIER_ORDER: AdaptiveTier[] = ["easy", "medium", "hard", "expert"];

function tierIndex(tier: string | null | undefined): number {
  const t = (tier ?? "").toLowerCase();
  const idx = TIER_ORDER.indexOf(t as AdaptiveTier);
  return idx >= 0 ? idx : 1; // default medium
}

function clampTier(idx: number): AdaptiveTier {
  return TIER_ORDER[Math.max(0, Math.min(TIER_ORDER.length - 1, idx))];
}

export function tierOfQuestion(q: AdaptiveQuestion): AdaptiveTier {
  return clampTier(tierIndex((q.adaptive_tier ?? q.difficulty) as string | null | undefined));
}

/**
 * Determine the learner's starting tier for a given beta lesson using recent
 * quiz_attempts as the primary signal. Falls back to "easy" for new learners.
 * Designed to be conservative — never starts a brand-new learner at hard/expert.
 */
export async function resolveStartingTier(
  userId: string | null | undefined,
  lessonId: string,
): Promise<AdaptiveTier> {
  if (!userId) return "easy";
  try {
    const { data } = await supabase
      .from("quiz_attempts")
      .select("score_percent, passed, created_at")
      .eq("user_id", userId)
      .eq("lesson_id", lessonId)
      .order("created_at", { ascending: false })
      .limit(3);
    const rows = (data ?? []) as Array<{ score_percent: number | null; passed: boolean | null }>;
    if (rows.length === 0) return "easy";
    const last = rows[0];
    const pct = Number(last.score_percent ?? 0);
    if (last.passed && pct >= 90) return "hard";
    if (last.passed && pct >= 80) return "medium";
    if (last.passed) return "medium";
    // Last attempt failed — reinforce at easy unless they have prior strong runs.
    const strongPrior = rows.some((r) => r.passed && Number(r.score_percent ?? 0) >= 80);
    return strongPrior ? "easy" : "easy";
  } catch {
    return "easy";
  }
}

/**
 * Build an adaptive 5-question sequence from a tier-tagged pool.
 *
 * Strategy:
 *  - 2 questions at the starting tier (confirm baseline)
 *  - 2 questions one tier above (stretch)
 *  - 1 question two tiers above (challenge / expert-ready)
 *
 * If a tier has no questions in the pool, we fall back to the closest tier
 * with available questions to keep the sequence at 5 items.
 */
export function buildAdaptiveSequence(
  pool: AdaptiveQuestion[],
  startingTier: AdaptiveTier,
): AdaptiveQuestion[] {
  return buildAdaptivePlan(pool, startingTier).sequence;
}

export interface AdaptivePlan {
  /** Ordered list of 5 questions to serve. */
  sequence: AdaptiveQuestion[];
  /** The tier originally planned for each slot in `sequence`. */
  plannedTiers: AdaptiveTier[];
  /** Tier-bucketed remaining pool (questions NOT placed in the initial sequence). */
  remainingByTier: Record<AdaptiveTier, AdaptiveQuestion[]>;
}

/**
 * Build the initial adaptive sequence AND return a remaining pool so the
 * caller (LessonScreen) can perform mid-quiz rescue swaps after consecutive
 * misses or consecutive correct answers.
 */
export function buildAdaptivePlan(
  pool: AdaptiveQuestion[],
  startingTier: AdaptiveTier,
): AdaptivePlan {
  const byTier: Record<AdaptiveTier, AdaptiveQuestion[]> = {
    easy: [], medium: [], hard: [], expert: [],
  };
  for (const q of pool) {
    const t = tierOfQuestion(q);
    byTier[t].push(q);
  }

  // light shuffle so the same two questions don't always appear in the same order
  for (const tier of TIER_ORDER) {
    byTier[tier] = [...byTier[tier]].sort(() => Math.random() - 0.5);
  }

  const startIdx = tierIndex(startingTier);
  const plan: AdaptiveTier[] = [
    clampTier(startIdx),
    clampTier(startIdx),
    clampTier(startIdx + 1),
    clampTier(startIdx + 1),
    clampTier(startIdx + 2),
  ];

  const used = new Set<string>();
  const out: AdaptiveQuestion[] = [];
  const plannedTiers: AdaptiveTier[] = [];

  const pickFrom = (tier: AdaptiveTier): AdaptiveQuestion | null => {
    const list = byTier[tier];
    const next = list.find((q) => !used.has(q.question_id));
    if (next) { used.add(next.question_id); return next; }
    // Fallback: search outward by tier distance
    for (let dist = 1; dist < TIER_ORDER.length; dist++) {
      for (const direction of [-1, 1]) {
        const alt = clampTier(tierIndex(tier) + direction * dist);
        const altPick = byTier[alt].find((q) => !used.has(q.question_id));
        if (altPick) { used.add(altPick.question_id); return altPick; }
      }
    }
    return null;
  };

  for (const tier of plan) {
    const q = pickFrom(tier);
    if (q) { out.push(q); plannedTiers.push(tier); }
  }

  // Build remaining-by-tier pool (everything not placed in initial sequence).
  const remainingByTier: Record<AdaptiveTier, AdaptiveQuestion[]> = {
    easy: [], medium: [], hard: [], expert: [],
  };
  for (const tier of TIER_ORDER) {
    remainingByTier[tier] = byTier[tier].filter((q) => !used.has(q.question_id));
  }
  return { sequence: out, plannedTiers, remainingByTier };
}

/**
 * Mid-quiz rescue swap. Replaces the question at `slotIdx` with one whose
 * tier is shifted by `direction` (-1 to soften, +1 to challenge), pulling
 * from `remainingByTier`. Falls back across tiers if the exact target is
 * empty, but only if a different question is actually available. Returns
 * the chosen replacement plus the tier it was served at, or null when no
 * suitable swap exists (in which case the planned question is kept).
 */
export function pickRescueReplacement(args: {
  currentQuestion: AdaptiveQuestion;
  targetTier: AdaptiveTier;
  remainingByTier: Record<AdaptiveTier, AdaptiveQuestion[]>;
}): { question: AdaptiveQuestion; servedTier: AdaptiveTier } | null {
  const targetIdx = tierIndex(args.targetTier);
  // Try exact tier first, then walk outward by distance.
  for (let dist = 0; dist < TIER_ORDER.length; dist++) {
    const candidates: AdaptiveTier[] = dist === 0
      ? [args.targetTier]
      : [clampTier(targetIdx - dist), clampTier(targetIdx + dist)];
    for (const tier of candidates) {
      const pool = args.remainingByTier[tier];
      if (!pool || pool.length === 0) continue;
      // Don't return the same question we're already showing.
      const pick = pool.find((q) => q.question_id !== args.currentQuestion.question_id);
      if (pick) {
        // Mutate pool so the same swap isn't reused for the next slot.
        const idx = pool.indexOf(pick);
        pool.splice(idx, 1);
        return { question: pick, servedTier: tier };
      }
    }
  }
  return null;
}

export function shiftTier(tier: AdaptiveTier, delta: number): AdaptiveTier {
  return clampTier(tierIndex(tier) + delta);
}

export function isBetaLesson(lessonId: string): boolean {
  return isLaunchLessonId(lessonId);
}

export const ADAPTIVE_TIERS = TIER_ORDER;